import React, { useEffect, useState } from "react";
import { Modal } from "../../../utils/Ui/Modal";
import { S3URL } from "../../../data/globalConstants";

export default function AlbumSection({ slug, title, items = [] }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [imageOrientations, setImageOrientations] = useState({});

  const isOpen = selectedIndex !== null;
  const selectedItem = isOpen ? items[selectedIndex] : null;

  const closeModal = () => {
    setSelectedIndex(null);
  };

  const goToPrev = () => {
    if (!items.length || selectedIndex === null) return;
    setSelectedIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goToNext = () => {
    if (!items.length || selectedIndex === null) return;
    setSelectedIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  // Orientation comes from the thumbnail the grid already renders. Measuring it
  // with a separate Image() downloaded every photo in the album up front, even
  // the ones off screen.
  const handleImageLoad = (key) => (event) => {
    const { naturalWidth, naturalHeight } = event.target;

    const orientation =
      naturalWidth > naturalHeight
        ? "landscape"
        : naturalHeight > naturalWidth
          ? "portrait"
          : "square";

    setImageOrientations((prev) =>
      prev[key] === orientation ? prev : { ...prev, [key]: orientation },
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrev();
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, items.length]);

  return (
    <section id={slug}>
      <div className="row">
        {items.map((item, index) => {
          const key = item.id || item.filename || index;
          const orientation = imageOrientations[key];
          const objectFit = orientation === "portrait" ? "contain" : "cover";

          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className="col-6 col-lg-4 btn p-0 border-0 shadow-none overflow-hidden p-1"
            >
              <div
                className="w-100 d-flex align-items-start justify-content-center overflow-hidden"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: "5px",
                  aspectRatio: "16 / 9",
                  maxHeight: "180px",
                }}
              >
                <img
                  src={S3URL + item.filename}
                  alt={item.alt || item.title || `Photo ${index + 1}`}
                  className="img-fluid"
                  loading="lazy"
                  decoding="async"
                  onLoad={handleImageLoad(key)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit,
                    objectPosition: "top center",
                    display: "block",
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title={title}
        size="threeQuarter"
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        {selectedItem && (
          <div className="container-fluid">
            <div className="row align-items-center justify-content-center">
              <div className="col-auto">
                <button
                  type="button"
                  onClick={goToPrev}
                  aria-label="Previous image"
                  className="gallery-nav-arrow btn btn-link p-0 d-flex align-items-center justify-content-center border-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="40"
                    width="40"
                    viewBox="0 -960 960 960"
                    fill="#ffffff"
                  >
                    <path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z" />
                  </svg>
                </button>
              </div>

              <div className="col text-center">
                <img
                  src={S3URL + selectedItem.filename}
                  alt={
                    selectedItem.alt || selectedItem.title || "Selected photo"
                  }
                  className="img-fluid"
                  style={{ maxHeight: "75vh", objectFit: "contain" }}
                />

                {selectedItem.caption && (
                  <div className="mt-3">{selectedItem.caption}</div>
                )}

                <div className="mt-2 small">
                  {selectedIndex + 1} / {items.length}
                </div>
              </div>

              <div className="col-auto">
                <button
                  type="button"
                  onClick={goToNext}
                  aria-label="Next image"
                  className="btn btn-link p-0 d-flex align-items-center justify-content-center border-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="40"
                    width="40"
                    viewBox="0 -960 960 960"
                    fill="#ffffff"
                  >
                    <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
